import { z } from "zod";
const PersistedSignatureEmailSchema = z.string().email();
const SignatureInputEmailSchema = PersistedSignatureEmailSchema.max(320);
// ─── Signature Field ───────────────────────────────────────
export const SignatureFieldSchema = z.object({
    id: z.string().uuid(),
    page: z.number().int().positive(),
    x: z.number().nonnegative(),
    y: z.number().nonnegative(),
    width: z.number().positive(),
    height: z.number().positive(),
    type: z.enum(["signature", "initials", "date", "text"]),
    assigneeEmail: PersistedSignatureEmailSchema,
    label: z.string().trim().min(1).max(160).optional(),
    placeholder: z.string().trim().min(1).max(240).optional(),
    required: z.boolean().default(true),
});
// ─── Signature Request ─────────────────────────────────────
export const SignatureRequestSchema = z.object({
    id: z.string().uuid(),
    documentId: z.string().uuid(),
    title: z.string().min(1).max(500),
    // `cancelled` is set by the firma webhook handler when the request
    // is cancelled or a recipient declines (`signing_request.cancelled`,
    // `signing_request.recipient.declined`). Omitting it from this enum
    // makes the API response fail Zod parsing on the client and the
    // entire signatures list silently disappears. See routes/webhooks.ts.
    status: z.enum([
        "pending",
        "sent",
        "partially_signed",
        "counter_sign_pending",
        "completed",
        "expired",
        "revoked",
        "cancelled",
    ]),
    fields: z.array(SignatureFieldSchema),
    signers: z.array(z.object({
        email: PersistedSignatureEmailSchema,
        name: z.string().min(1).max(200),
        order: z.number().int().positive().default(1),
        role: z.enum(["external", "internal"]).default("external"),
        inviteSentAt: z.string().datetime().nullable().optional(),
        signedAt: z.string().datetime().nullable(),
        ipAddress: z.string().nullable(),
        userAgent: z.string().nullable(),
    })),
    originalDocumentHash: z.string(),
    signedDocumentHash: z.string().nullable(),
    signedBlobKey: z.string().nullable(),
    auditTrailBlobKey: z.string().nullable(),
    expiresAt: z.string().datetime().nullable(),
    // `createdBy` is the actor who created the request. For staff-
    // created requests this is the requester's email; for system-job
    // creators (cron-driven webhook reconciliation, recovery scripts)
    // it's a synthetic identifier like `system@cron`. Strict
    // `z.string().email()` validation rejects the synthetic values and
    // — because Zod fails the whole list parse on one bad row — makes
    // the entire `/signatures` page render "No signature requests
    // found" whenever a single system-actor row exists in the tenant.
    // Same brittleness class as the templateFields-strip bug from
    // 2026-05-09; relaxed to `z.string().min(1)` to accept any non-
    // empty actor identifier. UI components that want to distinguish
    // can check format themselves.
    createdBy: z.string().min(1),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ─── Create ────────────────────────────────────────────────
export const SignatureRequestCreateSchema = z.object({
    documentId: z.string().uuid(),
    title: z.string().min(1).max(500),
    fields: z
        .array(SignatureFieldSchema.omit({ id: true }).extend({
        assigneeEmail: SignatureInputEmailSchema,
        value: z.string().min(1).max(200_000).optional(),
    }))
        .min(1),
    signers: z
        .array(z.object({
        email: SignatureInputEmailSchema,
        name: z.string().min(1).max(200),
        order: z.number().int().positive().optional(),
        role: z.enum(["external", "internal"]).optional(),
        preAuthorized: z.boolean().optional(),
    }))
        .min(1),
    expiresAt: z.string().datetime().optional(),
    message: z.string().max(2000).optional(),
    /** 'native' = in-house signing flow; 'firma' = Firma.dev for high-value contracts. */
    provider: z.enum(["native", "firma"]).default("native"),
});
// ─── Sign (public, no auth) ───────────────────────────────
export const SignatureSubmitSchema = z.object({
    requestId: z.string().uuid(),
    signerEmail: SignatureInputEmailSchema,
    token: z.string().min(1),
    fields: z.array(z.object({
        fieldId: z.string().min(1).max(200),
        value: z.string().max(200_000),
    })),
    consentGiven: z.literal(true, {
        errorMap: () => ({ message: "You must consent to sign electronically" }),
    }),
});
export const SignatureDeclineSchema = z.object({
    requestId: z.string().uuid(),
    signerEmail: SignatureInputEmailSchema,
    token: z.string().min(1),
    reason: z.string().max(1000).optional(),
});
export const SignatureInviteResendSchema = z.object({
    signerEmail: SignatureInputEmailSchema.optional(),
});
export const SignatureReminderRunSchema = z.object({
    olderThanHours: z.number().int().min(1).max(720).default(72),
    deadlineNoticeWithinHours: z.number().int().min(1).max(720).default(48),
    limit: z.number().int().min(1).max(100).default(50),
    dryRun: z.boolean().default(false),
});
export const SignatureExpiryRunSchema = z.object({
    limit: z.number().int().min(1).max(500).default(100),
    dryRun: z.boolean().default(false),
});
export const SignatureStatusReportQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(1000).default(500),
    staleAfterHours: z.coerce.number().int().min(1).max(720).default(72),
    deadlineWindowHours: z.coerce.number().int().min(1).max(720).default(48),
});
const SignatureExpiryCandidateSchema = z.object({
    requestId: z.string().uuid(),
    title: z.string().min(1),
    previousStatus: SignatureRequestSchema.shape.status,
    expiresAt: z.string().datetime(),
});
export const SignatureExpiryRunResponseSchema = z.object({
    checked: z.number().int(),
    dueRequests: z.number().int(),
    dryRun: z.boolean(),
    limit: z.number().int(),
    asOf: z.string().datetime(),
    candidates: z.array(SignatureExpiryCandidateSchema),
    expired: z.array(SignatureExpiryCandidateSchema),
});
// ─── Delivery Attempts ─────────────────────────────────────
export const SignatureDeliveryAttemptSchema = z.object({
    id: z.string().uuid(),
    requestId: z.string().uuid(),
    batchId: z.string().uuid(),
    channel: z.enum(["email"]),
    purpose: z.enum([
        "initial_invite",
        "resend_invite",
        "reminder",
        "deadline_notice",
        "completion_pack",
    ]),
    provider: z.string().min(1),
    sender: z.string().min(1),
    // A delivery attempt is a HISTORICAL record of the address used — loose
    // response field (loosen output, tighten input). Legacy / malformed values
    // ("foo@", over-320-char addresses) must round-trip for display + debugging,
    // not break the whole attempts list. Input paths (create / resend) still
    // validate against SignatureInputEmailSchema.
    recipientEmail: z.string(),
    recipientName: z.string().nullable(),
    status: z.enum(["sent", "failed"]),
    errorMessage: z.string().nullable(),
    details: z.record(z.string(), z.unknown()).nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const SignatureDeliveryAttemptListResponseSchema = z.object({
    attempts: z.array(SignatureDeliveryAttemptSchema),
    total: z.number().int(),
});
// ─── Status Report ─────────────────────────────────────────
export const SignatureStatusReportRowSchema = z.object({
    requestId: z.string().uuid(),
    title: z.string().min(1),
    status: SignatureRequestSchema.shape.status,
    provider: z.string().min(1),
    createdBy: z.string().min(1),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    expiresAt: z.string().datetime().nullable(),
    pendingAgeHours: z.number().nonnegative().nullable(),
    hoursUntilExpiry: z.number().nullable(),
    currentOrder: z.number().int().positive().nullable(),
    currentSignerEmails: z.array(PersistedSignatureEmailSchema),
    lastEvent: z
        .object({
        event: z.string().min(1),
        actor: z.string().min(1),
        createdAt: z.string().datetime(),
    })
        .nullable(),
    latestDelivery: z
        .object({
        purpose: SignatureDeliveryAttemptSchema.shape.purpose,
        status: SignatureDeliveryAttemptSchema.shape.status,
        recipientEmail: PersistedSignatureEmailSchema,
        errorMessage: z.string().nullable(),
        createdAt: z.string().datetime(),
    })
        .nullable(),
    unresolvedFailureCount: z.number().int().nonnegative(),
    failedRecipients: z.array(z.object({
        email: PersistedSignatureEmailSchema,
        purpose: SignatureDeliveryAttemptSchema.shape.purpose,
        errorMessage: z.string().nullable(),
        createdAt: z.string().datetime(),
    })),
    statusLevel: z.enum(["ok", "info", "warning", "danger"]),
    primaryReason: z.enum([
        "completed",
        "terminal",
        "overdue",
        "delivery_failure",
        "no_invite_sent",
        "stale_active",
        "deadline_due",
        "awaiting_counter_sign",
        "waiting_on_signer",
        "pending_setup",
    ]),
    action: z.string().min(1),
});
export const SignatureStatusReportResponseSchema = z.object({
    generatedAt: z.string().datetime(),
    thresholds: z.object({
        staleAfterHours: z.number().int(),
        deadlineWindowHours: z.number().int(),
    }),
    summary: z.object({
        totalRequests: z.number().int().nonnegative(),
        activeRequests: z.number().int().nonnegative(),
        completedRequests: z.number().int().nonnegative(),
        terminalRequests: z.number().int().nonnegative(),
        overdueRequests: z.number().int().nonnegative(),
        deadlineDueRequests: z.number().int().nonnegative(),
        deliveryFailureRequests: z.number().int().nonnegative(),
        staleRequests: z.number().int().nonnegative(),
        stuckRequests: z.number().int().nonnegative(),
        waitingOnCounterSignRequests: z.number().int().nonnegative(),
    }),
    rows: z.array(SignatureStatusReportRowSchema),
});
// ─── Audit Trail ───────────────────────────────────────────
export const SignatureAuditEntrySchema = z.object({
    timestamp: z.string().datetime(),
    event: z.enum([
        "request_created",
        "email_sent",
        "document_viewed",
        "consent_given",
        "field_signed",
        "recipient_declined",
        "signer_completed",
        "counter_sign_pending",
        "counter_signed",
        "completion_email_sent",
        "document_completed",
        "document_expired",
        "document_revoked",
    ]),
    actor: z.string(),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    details: z.record(z.string(), z.unknown()).optional(),
});
// ─── Firma Webhook Envelope ────────────────────────────────
//
// Wire shape of the inbound webhook Firma posts to `/api/webhooks/firma`.
// Keys stay snake_case to match the provider payload byte-for-byte (the
// raw body is HMAC-signed and stored verbatim as the audit `details`).
// Every field is optional + `.passthrough()` keeps unmodelled keys: the
// envelope carries more than we consume, and a legitimate event must never
// be rejected for shipping an extra field. Validation here only rejects a
// non-object payload (e.g. a JSON array/string) before it reaches the
// service. Event `type` uses dotted names (`signing_request.completed`, …).
export const FirmaWebhookRecipientSchema = z
    .object({
    email: z.string().optional(),
    signed_at: z.string().nullable().optional(),
})
    .passthrough();
export const FirmaWebhookSchema = z
    .object({
    id: z.string().optional(),
    type: z.string().optional(),
    created_at: z.string().optional(),
    data: z
        .object({
        signing_request: z
            .object({
            id: z.string().optional(),
            name: z.string().optional(),
            sent_at: z.string().nullable().optional(),
            completed_at: z.string().nullable().optional(),
        })
            .passthrough()
            .optional(),
        recipients: z.array(FirmaWebhookRecipientSchema).optional(),
    })
        .passthrough()
        .optional(),
})
    .passthrough();
// ─── Response ──────────────────────────────────────────────
export const SignatureRequestListResponseSchema = z.object({
    requests: z.array(SignatureRequestSchema),
    total: z.number().int(),
});
//# sourceMappingURL=signatures.js.map